import React, { useId, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";
import useDialogFocus, { openDialogCount } from "./useDialogFocus";

// Gedeeld frame voor Modal, Drawer en ConfirmDialog: portal naar document.body, overlay,
// role="dialog" + aria-modal + aria-labelledby (de zichtbare titel), focus-trap, startfocus,
// focus terug naar de trigger en Esc (zie useDialogFocus). Z-index via tokens: modal
// --z-overlay, drawer --z-drawer (zie components.css). Opent de dialoog terwijl er al een
// andere open staat (bv. een Modal vanuit een Drawer), of is `stacked` gezet (ConfirmDialog),
// dan krijgt de overlay --z-dialog en ligt hij er dus altijd bovenop.
//
// `onClose` ontbreekt/undefined = niet sluitbaar (bv. terwijl een ConfirmDialog bezig is):
// Esc, overlayklik en de ×-knop doen dan niets.
//
// variant "full" = het hele venster (12px rand op desktop, 0 op gsm; zie components.css), bv. de
// documentviewer. `headerActions` = extra knoppen in de kop, links van ×.
export default function ModalFrame({
  open,
  title,
  onClose,
  footer,
  width,
  variant = "modal", // "modal" | "drawer" | "full"
  initialFocusRef,
  describedBy,
  className = "",
  bodyClassName = "",
  stacked = false,
  headerActions = null,
  children,
}) {
  const containerRef = useRef(null);
  const titleId = useId();
  // Bepaald op het moment van openen: de stapel bevat dan enkel de dialogen die al open
  // stonden (deze dialoog registreert zich pas in zijn effect).
  const nested = useMemo(() => open && openDialogCount() > 0, [open]);
  const onTop = stacked || nested;

  useDialogFocus({ open, containerRef, onEscape: onClose, initialFocusRef });

  if (!open) return null;

  const hasTitle = title != null && title !== "";
  const sizeVar = variant === "drawer" ? "--drawer-width" : "--modal-width";
  const sized = width && variant !== "full";

  return createPortal(
    <div
      role="presentation"
      className={`dialog-overlay dialog-overlay--${variant}${onTop ? " dialog-overlay--stacked" : ""}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={hasTitle ? titleId : undefined}
        aria-describedby={describedBy}
        tabIndex={-1}
        className={`dialog dialog--${variant} ${className}`.trim()}
        style={sized ? { [sizeVar]: `${width}px` } : undefined}
      >
        <div className="dialog__header">
          <h2 id={titleId} className="dialog__title">
            {title}
          </h2>
          {headerActions ? <div className="dialog__header-actions">{headerActions}</div> : null}
          <button
            type="button"
            className="dialog__close"
            aria-label="Sluiten"
            onClick={() => onClose?.()}
            disabled={!onClose}
          >
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className={`dialog__body ${bodyClassName}`.trim()} data-dialog-body="">
          {children}
        </div>
        {footer ? <div className="dialog__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body
  );
}
